import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Lucide from "@/components/Base/Lucide";
import { Menu, Popover } from "@/components/Base/Headless";
import Pagination from "@/components/Base/Pagination";
import { FormInput, FormSelect } from "@/components/Base/Form";
import Button from "@/components/Base/Button";
import Table from "@/components/Base/Table";

type OutletType = {
    id: string;
    typeName: string;
    companyId?: string;
    createdAt: string;
    updatedAt: string;
};

function OutletTypePage() {
    const [outletTypes, setOutletTypes] = useState<OutletType[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortField, setSortField] = useState<'typeName' | 'createdAt' | 'updatedAt'>('createdAt');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const companyId = new URLSearchParams(window.location.search).get("companyId");
                const queryParams = new URLSearchParams();
                if (companyId) queryParams.append("companyId", companyId);
                if (searchQuery) queryParams.append("q", searchQuery);
                queryParams.append("page", currentPage.toString());
                queryParams.append("pageSize", pageSize.toString());
                queryParams.append("sortBy", sortField);
                queryParams.append("sortOrder", sortOrder);

                const res = await fetch(
                    `${import.meta.env.VITE_API_URL}/outlet-types?${queryParams.toString()}`,
                    {
                        headers: { Authorization: `Bearer ${getToken()}` }
                    }
                );
                const data = await res.json();
                setOutletTypes(Array.isArray(data?.data) ? data.data : []);
                setTotalPages(data.totalPages || 1);
                setTotal(data.total || 0);
            } catch (err) {
                console.error("Failed to fetch outlet types:", err);
            } finally {
                setLoading(false);
            }
        })();
    }, [currentPage, pageSize, searchQuery, sortField, sortOrder]);

    const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setCurrentPage(1);
    };

    const handleSort = (field: 'typeName' | 'createdAt' | 'updatedAt') => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
        setCurrentPage(1);
    };

    const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handleFirstPage = () => {
        setCurrentPage(1);
    };

    const handleLastPage = () => {
        setCurrentPage(totalPages);
    };

    const handleDelete = async (id: string) => {
        if (!confirm("Are you sure you want to delete this outlet type?")) return;

        try {
            const res = await fetch(`${import.meta.env.VITE_API_URL}/outlet-types/${id}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${getToken()}`
                }
            });

            if (res.ok) {
                setCurrentPage(1);
            } else {
                const error = await res.json();
                alert(`Failed to delete outlet type: ${error?.error || "Unknown error"}`);
            }
        } catch (err) {
            console.error("Delete failed", err);
            alert("Something went wrong deleting outlet type");
        }
    };

    if (loading && outletTypes.length === 0) {
        return (
            <ProtectedRoute>
                <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <Lucide icon="Loader2" className="w-8 h-8 animate-spin mx-auto mb-2" />
                            <div>Loading outlet types...</div>
                        </div>
                    </div>
                </RoleGate>
            </ProtectedRoute>
        );
    }

    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                <div className="grid grid-cols-12 gap-y-10 gap-x-6">
                    <div className="col-span-12">
                        <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row">
                            <div className="text-base font-medium group-[.mode--light]:text-white">
                                Outlet Types
                                {total > 0 && (
                                    <span className="ml-2 text-sm font-normal text-white">
                                        ({total} {total === 1 ? 'type' : 'types'})
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                                <Button
                                    variant="primary"
                                    className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                                    onClick={() => {
                                        const searchParams = new URLSearchParams(window.location.search);
                                        const companyId = searchParams.get("companyId");
                                        const projectId = searchParams.get("projectId");
                                        
                                        let addUrl = "/outlet-types/add";
                                        if (projectId) {
                                            addUrl += `?projectId=${projectId}`;
                                        } else if (companyId) {
                                            addUrl += `?companyId=${companyId}`;
                                        }
                                        
                                        navigate(addUrl);
                                    }}
                                >
                                    <Lucide icon="PenLine" className="stroke-[1.3] w-4 h-4 mr-2" />
                                    Add New Outlet Type
                                </Button>
                            </div>
                        </div>
                        <div className="mt-3.5">
                            <div className="flex flex-col box box--stacked">
                                <div className="flex flex-col p-5 sm:items-center sm:flex-row gap-y-2">
                                    <div>
                                        <form onSubmit={handleSearch}>
                                            <div className="relative">
                                                <Lucide
                                                    icon="Search"
                                                    className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                                                />
                                                <FormInput
                                                    type="text"
                                                    placeholder="Search outlet types..."
                                                    className="pl-9 sm:w-64 rounded-[0.5rem]"
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                            </div>
                                        </form>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 sm:ml-auto">
                                        <Menu>
                                            <Menu.Button
                                                as={Button}
                                                variant="outline-secondary"
                                                className="w-full sm:w-auto"
                                            >
                                                <Lucide
                                                    icon="Download"
                                                    className="stroke-[1.3] w-4 h-4 mr-2"
                                                />
                                                Export
                                                <Lucide
                                                    icon="ChevronDown"
                                                    className="stroke-[1.3] w-4 h-4 ml-2"
                                                />
                                            </Menu.Button>
                                            <Menu.Items className="w-40">
                                                <Menu.Item>
                                                    <Lucide icon="FileBarChart" className="w-4 h-4 mr-2" />
                                                    PDF
                                                </Menu.Item>
                                                <Menu.Item>
                                                    <Lucide icon="FileBarChart" className="w-4 h-4 mr-2" />
                                                    CSV
                                                </Menu.Item>
                                            </Menu.Items>
                                        </Menu>
                                    </div>
                                </div>
                                <div className="relative overflow-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
                                    <Table className="border-b border-slate-200/60 text-xs">
                                        <Table.Thead className="text-xs sticky top-0 z-[55] bg-white dark:bg-darkmode-600 shadow-sm border-b border-slate-200/60">
                                            <Table.Tr>
                                                <Table.Td 
                                                    className="py-1.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none bg-white dark:bg-darkmode-600"
                                                    onClick={() => handleSort('typeName')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Type Name
                                                        <Lucide 
                                                            icon={sortField === 'typeName' 
                                                                ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown')
                                                                : 'ArrowUpDown'
                                                            } 
                                                            className={`w-3 h-3 ${sortField === 'typeName' ? '' : 'text-slate-400'}`}
                                                        />
                                                    </div>
                                                </Table.Td>
                                                <Table.Td 
                                                    className="py-1.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none bg-white dark:bg-darkmode-600"
                                                    onClick={() => handleSort('createdAt')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Created At
                                                        <Lucide 
                                                            icon={sortField === 'createdAt' 
                                                                ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown')
                                                                : 'ArrowUpDown'
                                                            } 
                                                            className={`w-3 h-3 ${sortField === 'createdAt' ? '' : 'text-slate-400'}`}
                                                        />
                                                    </div>
                                                </Table.Td>
                                                <Table.Td 
                                                    className="py-1.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none bg-white dark:bg-darkmode-600"
                                                    onClick={() => handleSort('updatedAt')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Updated At
                                                        <Lucide 
                                                            icon={sortField === 'updatedAt' 
                                                                ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown')
                                                                : 'ArrowUpDown'
                                                            } 
                                                            className={`w-3 h-3 ${sortField === 'updatedAt' ? '' : 'text-slate-400'}`}
                                                        />
                                                    </div>
                                                </Table.Td>
                                                <Table.Td className="text-right py-1.5 bg-white dark:bg-darkmode-600">Action</Table.Td>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {outletTypes.map((type) => (
                                                <Table.Tr key={type.id} className="[&_td]:last:border-b-0">
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {type.typeName}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {new Date(type.createdAt).toLocaleDateString()}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {new Date(type.updatedAt).toLocaleDateString()}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 text-right border-dashed dark:bg-darkmode-600 text-xs">
                                                        <div className="flex justify-end items-center gap-3">
                                                            <Link
                                                                to={(() => {
                                                                    const searchParams = new URLSearchParams(window.location.search);
                                                                    const projectId = searchParams.get("projectId");
                                                                    const companyId = searchParams.get("companyId");
                                                                    
                                                                    let editUrl = `/outlet-types/${type.id}/edit`;
                                                                    if (projectId) {
                                                                        editUrl += `?projectId=${projectId}`;
                                                                    } else if (companyId) {
                                                                        editUrl += `?companyId=${companyId}`;
                                                                    }
                                                                    
                                                                    return editUrl;
                                                                })()}
                                                                className="btn btn-outline-secondary p-2 rounded-lg"
                                                                title="Edit"
                                                            >
                                                                <Lucide icon="Pencil" className="w-3 h-3" />
                                                            </Link>
                                                            <button
                                                                onClick={() => handleDelete(type.id)}
                                                                className="btn btn-outline-danger p-2 rounded-lg"
                                                                title="Delete"
                                                            >
                                                                <Lucide icon="Trash2" className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    </Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </div>
                                <div className="flex flex-col-reverse flex-wrap items-center p-5 flex-reverse gap-y-2 sm:flex-row">
                                    <div className="text-sm text-slate-600 dark:text-slate-400 mr-4">
                                        {total > 0 ? (
                                            <>
                                                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, total)} of {total} outlet types
                                            </>
                                        ) : (
                                            'No outlet types found'
                                        )}
                                    </div>
                                    <Pagination className="flex-1 w-full mr-auto sm:w-auto">
                                        <Pagination.Link 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (currentPage > 1) handleFirstPage();
                                            }}
                                            className={currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronsLeft" className="w-3 h-3" />
                                        </Pagination.Link>
                                        <Pagination.Link 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (currentPage > 1) handlePrevPage();
                                            }}
                                            className={currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronLeft" className="w-3 h-3" />
                                        </Pagination.Link>
                                        {totalPages > 0 && (
                                            <>
                                                {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                                                    let pageNum: number;
                                                    if (totalPages <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage >= totalPages - 2) {
                                                        pageNum = totalPages - 4 + i;
                                                    } else {
                                                        pageNum = currentPage - 2 + i;
                                                    }
                                                    return (
                                                        <Pagination.Link
                                                            key={pageNum}
                                                            active={currentPage === pageNum}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setCurrentPage(pageNum);
                                                            }}
                                                            className="cursor-pointer"
                                                        >
                                                            {pageNum}
                                                        </Pagination.Link>
                                                    );
                                                })}
                                            </>
                                        )}
                                        <Pagination.Link 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (currentPage < totalPages) handleNextPage();
                                            }}
                                            className={currentPage >= totalPages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronRight" className="w-3 h-3" />
                                        </Pagination.Link>
                                        <Pagination.Link 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (currentPage < totalPages) handleLastPage();
                                            }}
                                            className={currentPage >= totalPages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronsRight" className="w-3 h-3" />
                                        </Pagination.Link>
                                    </Pagination>
                                    <FormSelect 
                                        className="sm:w-20 rounded-[0.5rem]"
                                        value={pageSize}
                                        onChange={handlePageSizeChange}
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                    </FormSelect>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </RoleGate>
        </ProtectedRoute>
    );
}

export default OutletTypePage;
